import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { SafeCryptoLogo } from '@/components/SafeCryptoLogo';
import {
  LayoutDashboard,
  Wallet,
  ArrowLeftRight,
  ShieldCheck,
  Users,
  Settings,
  LogOut,
  Gift,
  ChevronLeft,
  ChevronRight,
  Shield,
  CreditCard,
  Coins,
  Home,
  X,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export function Sidebar({ mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const collapsed = false;

  const isAdmin = user?.role === 'admin';

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/wallet', icon: Wallet, label: t('nav.wallet') },
    { path: '/exchange', icon: ArrowLeftRight, label: t('nav.exchange') },
    { path: '/cards', icon: CreditCard, label: t('nav.cards') },
    { path: '/safecoin', icon: Coins, label: 'SafeCoin' },
    { path: '/leaderboard', icon: Gift, label: t('nav.leaderboard') },
    { path: '/kyc', icon: ShieldCheck, label: t('nav.kyc') },
    { path: '/referral', icon: Users, label: t('nav.referral') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const adminItems = isAdmin
    ? [{ path: '/admin', icon: Shield, label: t('nav.admin') }]
    : [];

  const allItems = [...menuItems, ...adminItems];

  const handleNavClick = (path) => {
    navigate(path);
    onMobileClose?.();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onMobileClose?.();
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed top-0 left-0 h-screen bg-[hsl(var(--card))] border-r border-[hsl(var(--border))] flex flex-col z-50 transition-transform duration-300',
          'w-[260px] lg:w-[240px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-[hsl(var(--border))]">
          <Link to="/" className="flex items-center gap-2.5" onClick={onMobileClose}>
            <SafeCryptoLogo size={28} />
            <span className="font-semibold text-base tracking-tight font-['Space_Grotesk']">Safe Crypto</span>
          </Link>
          {/* Mobile close button */}
          <button
            className="lg:hidden p-1.5 rounded-lg hover:bg-white/5 transition-colors"
            onClick={onMobileClose}
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {/* Home Link */}
          <button
            onClick={() => handleNavClick('/')}
            data-testid="nav-home"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/5"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            <span>{t('nav.home')}</span>
          </button>

          <div className="border-b border-[hsl(var(--border))]/30 mx-2 my-2" />

          {allItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNavClick(item.path)}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200',
                      active
                        ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/5'
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />}
                  </button>
                </TooltipTrigger>
              </Tooltip>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-[hsl(var(--border))]">
          <button
            onClick={handleLogout}
            data-testid="nav-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/5 transition-colors duration-200"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
